// Signal decoding: raw bit extraction (with two's-complement handling for
// signed signals) and physical-value conversion. Uses BigInt for the raw
// integer so signals up to the full 64-bit frame width decode exactly,
// rather than silently losing precision or wrapping through 32-bit ops.
import { getSignalBitPositions } from './bitMapping';

export function decodeSignalRaw(dataBytes, signal) {
  const positions = getSignalBitPositions(signal.startBit, signal.length, signal.byteOrder); // MSB..LSB
  let rawUnsigned = 0n;
  for (const pos of positions) {
    const byteValue = dataBytes[pos.byteIndex] || 0;
    const bitValue = (byteValue >> pos.bitInByte) & 1;
    rawUnsigned = (rawUnsigned << 1n) | BigInt(bitValue);
  }
  let raw = rawUnsigned;
  if (signal.signed && signal.length > 0) {
    const signBit = 1n << BigInt(signal.length - 1);
    if (rawUnsigned >= signBit) raw = rawUnsigned - (1n << BigInt(signal.length));
  }
  return { rawUnsigned, raw, positions };
}

export function formatBinary(rawUnsigned, length) {
  const bits = rawUnsigned.toString(2).padStart(length, '0');
  const groups = [];
  for (let end = bits.length; end > 0; end -= 4) groups.unshift(bits.slice(Math.max(0, end - 4), end));
  return groups.join(' ');
}

export function formatHex(rawUnsigned, length) {
  const hexDigits = Math.max(1, Math.ceil(length / 4));
  return '0x' + rawUnsigned.toString(16).toUpperCase().padStart(hexDigits, '0');
}

// Raw integer range representable by a signal's length/signedness.
export function computeRawRange(signal) {
  const max = signal.signed
    ? (1n << BigInt(signal.length - 1)) - 1n
    : (1n << BigInt(signal.length)) - 1n;
  const min = signal.signed ? -(1n << BigInt(signal.length - 1)) : 0n;
  return { min, max };
}

// Physical = Raw x Factor + Offset. Min/max are derived from the raw range
// rather than assumed ascending, since a negative Factor would invert them.
export function computePhysicalRange(signal) {
  const { min, max } = computeRawRange(signal);
  const a = Number(min) * signal.factor + signal.offset;
  const b = Number(max) * signal.factor + signal.offset;
  return { min: Math.min(a, b), max: Math.max(a, b) };
}

// Physical -> raw (the reverse of decode): Raw = round((Physical - Offset) / Factor).
// The input is first clamped to the signal's own documented Physical Min/Max (the
// engineer-specified valid range, e.g. a signal whose bits could represent 0-655.35
// km/h but is documented as only valid 0-250 km/h), then the resulting raw value is
// clamped to what the bit width can represent, as a defensive second pass. Returns
// the clamped signed raw (BigInt) plus its unsigned bit-pattern representation (for
// writing into bytes), and whether either clamp was applied.
export function computeRawFromPhysical(signal, physicalValue) {
  let clamped = false;
  let clampedPhysical = physicalValue;
  if (Number.isFinite(signal.physicalMin) && clampedPhysical < signal.physicalMin) {
    clampedPhysical = signal.physicalMin;
    clamped = true;
  }
  if (Number.isFinite(signal.physicalMax) && clampedPhysical > signal.physicalMax) {
    clampedPhysical = signal.physicalMax;
    clamped = true;
  }

  const rawFloat = (clampedPhysical - signal.offset) / signal.factor;
  let raw = BigInt(Math.round(rawFloat));
  const { min, max } = computeRawRange(signal);
  if (raw > max) { raw = max; clamped = true; }
  if (raw < min) { raw = min; clamped = true; }
  const rawUnsigned = raw < 0n ? raw + (1n << BigInt(signal.length)) : raw;
  return { raw, rawUnsigned, clamped };
}

// Writes a signal's raw bit pattern into a copy of `dataBytes`, at exactly the
// physical positions getSignalBitPositions() reports for it — the same mapping
// decode uses, so encode/decode are always consistent for a given byte order.
export function writeSignalRawIntoBytes(dataBytes, signal, rawUnsigned) {
  const positions = getSignalBitPositions(signal.startBit, signal.length, signal.byteOrder); // MSB..LSB
  const next = [...dataBytes];
  positions.forEach((pos, i) => {
    const bitValue = Number((rawUnsigned >> BigInt(signal.length - 1 - i)) & 1n);
    const mask = 1 << pos.bitInByte;
    next[pos.byteIndex] = bitValue ? (next[pos.byteIndex] | mask) & 0xff : next[pos.byteIndex] & ~mask & 0xff;
  });
  return next;
}

export function decodeSignal(dataBytes, signal) {
  const { rawUnsigned, raw, positions } = decodeSignalRaw(dataBytes, signal);
  const physical = Number(raw) * signal.factor + signal.offset;
  const physicalRange = computePhysicalRange(signal);
  return {
    raw,
    rawUnsigned,
    physical,
    hex: formatHex(rawUnsigned, signal.length),
    binary: formatBinary(rawUnsigned, signal.length),
    positions,
    physicalMin: physicalRange.min,
    physicalMax: physicalRange.max,
  };
}
